import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ClientCreateRequest,
  ClientDetail,
  ClientExport,
  ClientHistoryResponse,
  ClientListResponse,
  ClientUpdateRequest,
} from '@crm/shared';
import { api } from '../../lib/api';

export type ClientListFilter = {
  search?: string;
  page: number;
  pageSize: number;
  includeAnonymized: 'true' | 'false';
};

export const clientsKeys = {
  all: ['clients'] as const,
  list: (f: ClientListFilter) => ['clients', 'list', f] as const,
  one: (id: number) => ['clients', 'one', id] as const,
};

export function useClientsList(filter: ClientListFilter) {
  return useQuery({
    queryKey: clientsKeys.list(filter),
    queryFn: async () => {
      const params: Record<string, string> = {
        page: String(filter.page),
        pageSize: String(filter.pageSize),
        includeAnonymized: filter.includeAnonymized,
      };
      if (filter.search) params.search = filter.search;
      const res = await api.get<ClientListResponse>('/clients', { params });
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
}

export function useClientDetail(id: number | null) {
  return useQuery({
    queryKey: id ? clientsKeys.one(id) : ['clients', 'one', 'noop'],
    enabled: id !== null,
    queryFn: async () => {
      const res = await api.get<ClientDetail>(`/clients/${id}`);
      return res.data;
    },
  });
}

export function useClientCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ClientCreateRequest) => {
      const res = await api.post<ClientDetail>('/clients', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: clientsKeys.all }),
  });
}

export function useClientUpdate(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ClientUpdateRequest) => {
      const res = await api.patch<ClientDetail>(`/clients/${id}`, input);
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(clientsKeys.one(id), data);
      qc.invalidateQueries({ queryKey: clientsKeys.all });
    },
  });
}

export function useClientAnonymize(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.post<ClientDetail>(`/clients/${id}/anonymize`);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: clientsKeys.all }),
  });
}

export async function downloadClientExport(id: number): Promise<ClientExport> {
  const res = await api.get<ClientExport>(`/clients/${id}/export`);
  return res.data;
}

export type ClientHistoryFilter = {
  search?: string;
  page: number;
  pageSize: number;
};

export const clientHistoryKey = (id: number, filter: ClientHistoryFilter) =>
  ['clients', 'history', id, filter] as const;

export function useClientHistory(
  id: number | null,
  filter: ClientHistoryFilter,
) {
  return useQuery({
    queryKey:
      id !== null ? clientHistoryKey(id, filter) : ['clients', 'history', 'noop'],
    enabled: id !== null,
    queryFn: async () => {
      const params: Record<string, string> = {
        page: String(filter.page),
        pageSize: String(filter.pageSize),
      };
      if (filter.search) params.search = filter.search;
      const res = await api.get<ClientHistoryResponse>(`/clients/${id}/history`, {
        params,
      });
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
}
